/**
 * App-scoped watcher for "the server is serving a different bundle than I am".
 *
 *   start ──► check ──► unknown ──► current(sig)
 *               ▲                      │  served ≠ sig
 *               │                      ▼
 *      every 15 min while visible    STALE  ── terminal, polling stops
 *      or on tab-visible (≥60 s)       │
 *                                      └─► onStale fires exactly once
 *
 * The monitor is a module singleton rather than React state on purpose: the
 * route-scoped `EditingStateProvider` remounts its whole subtree on every
 * navigation, so component-held staleness would be forgotten precisely at the
 * moment it becomes actionable.
 */

import {
  BUILD_SIGNATURE_MIN_CHECK_INTERVAL_MS,
  BUILD_SIGNATURE_POLL_INTERVAL_MS,
  type ReadServedBuildSignature,
} from './buildSignature';

export type StaleBundleStatus =
  | { kind: 'unknown' }
  | { kind: 'current'; runningSignature: string }
  | { kind: 'stale'; runningSignature: string; servedSignature: string };

export type StaleBundleDetail = { runningSignature: string; servedSignature: string };

export type StaleBundleMonitor = {
  start: () => void;
  stop: () => void;
  /** Run one check now, ignoring the cadence. Resolves with the status after it. */
  check: () => Promise<StaleBundleStatus>;
  getStatus: () => StaleBundleStatus;
  subscribe: (listener: () => void) => () => void;
};

export type StaleBundleMonitorOptions = {
  readServedSignature: ReadServedBuildSignature;
  onStale?: (detail: StaleBundleDetail) => void;
  pollIntervalMs?: number;
  minCheckIntervalMs?: number;
  isVisible?: () => boolean;
  subscribeVisibility?: (onVisible: () => void) => () => void;
  now?: () => number;
};

const subscribeDocumentVisibility = (onVisible: () => void) => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') onVisible();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
};

export function createStaleBundleMonitor({
  readServedSignature,
  onStale,
  pollIntervalMs = BUILD_SIGNATURE_POLL_INTERVAL_MS,
  minCheckIntervalMs = BUILD_SIGNATURE_MIN_CHECK_INTERVAL_MS,
  isVisible = () => document.visibilityState === 'visible',
  subscribeVisibility = subscribeDocumentVisibility,
  now = () => Date.now(),
}: StaleBundleMonitorOptions): StaleBundleMonitor {
  let status: StaleBundleStatus = { kind: 'unknown' };
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let unsubscribeVisibility: (() => void) | null = null;
  let inFlight: Promise<StaleBundleStatus> | null = null;
  let lastCheckStartedAt = Number.NEGATIVE_INFINITY;

  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of listeners) listener();
  };

  const stop = () => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    unsubscribeVisibility?.();
    unsubscribeVisibility = null;
  };

  const applyServedSignature = (servedSignature: string) => {
    if (status.kind === 'unknown') {
      // First successful read is the baseline: whatever is served at boot is
      // what this tab is running.
      status = { kind: 'current', runningSignature: servedSignature };
      emit();
      return;
    }

    if (status.kind !== 'current' || status.runningSignature === servedSignature) return;

    status = { kind: 'stale', runningSignature: status.runningSignature, servedSignature };
    // Staleness is terminal — a later deploy cannot make this tab less stale.
    stop();
    emit();
    onStale?.({ runningSignature: status.runningSignature, servedSignature });
  };

  const check = (): Promise<StaleBundleStatus> => {
    if (status.kind === 'stale') return Promise.resolve(status);
    if (inFlight) return inFlight;

    lastCheckStartedAt = now();
    inFlight = readServedSignature()
      .then((servedSignature) => {
        // A failed or unreadable fetch must never be read as a deploy.
        if (servedSignature !== null) applyServedSignature(servedSignature);
        return status;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  };

  const start = () => {
    if (pollTimer !== null) return;

    void check();

    pollTimer = setInterval(() => {
      if (isVisible()) void check();
    }, pollIntervalMs);

    unsubscribeVisibility = subscribeVisibility(() => {
      if (now() - lastCheckStartedAt >= minCheckIntervalMs) void check();
    });
  };

  return {
    check,
    getStatus: () => status,
    start,
    stop,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Only a deployed build has content-hashed assets to compare, and only a
 * deployed build can go stale underneath an open tab.
 */
export const IS_DEPLOYED_BUILD = process.env.NODE_ENV === 'production';
